import React, { useMemo, useState } from "react";
import {
  ChevronLeft, ChevronRight, TrendingUp, TrendingDown, PiggyBank, Activity,
  CreditCard, Percent, Lightbulb, ArrowRight, CalendarDays,
} from "lucide-react";
import { monthlySummary } from "@/lib/insights";
import { formatCompact } from "@/lib/currency";
import { emergencyBasisLabel } from "@/lib/financeMetrics";

const STATUS = {
  sehat: "bg-emerald-100 text-emerald-700",
  waspada: "bg-amber-100 text-amber-700",
  perhatian: "bg-rose-100 text-rose-700",
};
const BAND_TEXT = { sehat: "text-emerald-600", waspada: "text-amber-600", kritis: "text-rose-600" };

function pctStr(v) {
  if (v == null) return "–";
  return `${Math.round(v * 100)}%`;
}

function Metric({ Icon, label, value, valueClass = "text-zinc-900", testId }) {
  return (
    <div className="rounded-2xl bg-zinc-50 p-3" data-testid={testId}>
      <div className="flex items-center gap-1 text-zinc-400">
        <Icon className="h-3 w-3" />
        <span className="text-[10px] font-semibold">{label}</span>
      </div>
      <p className={`mt-1 whitespace-nowrap font-mono text-sm font-bold ${valueClass}`}>{value}</p>
    </div>
  );
}

export function MonthlySummary({ finance, onSetup }) {
  const [offset, setOffset] = useState(0);
  const s = useMemo(() => monthlySummary(finance, offset), [finance, offset]);

  const canPrev = offset < s.totalHistory - 1;
  const canNext = offset > 0;
  const [mon, yr] = s.monthLabel.split(" ");
  const shortLabel = `${mon.slice(0, 3)} ${yr}`;

  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="monthly-summary-card">
      {/* Header + month nav */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <CalendarDays className="h-4 w-4 shrink-0 text-zinc-400" />
          <p className="whitespace-nowrap font-heading text-sm font-bold text-zinc-900">Ringkasan Bulanan</p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            data-testid="summary-prev-month"
            disabled={!canPrev}
            onClick={() => canPrev && setOffset((o) => o + 1)}
            className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-500 disabled:opacity-25"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[62px] text-center text-[11px] font-semibold text-zinc-700" data-testid="summary-month-label">
            {shortLabel}
          </span>
          <button
            data-testid="summary-next-month"
            disabled={!canNext}
            onClick={() => canNext && setOffset((o) => o - 1)}
            className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-500 disabled:opacity-25"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!s.hasData ? (
        <div className="rounded-2xl border border-dashed border-zinc-200 p-6 text-center" data-testid="summary-empty">
          <p className="text-sm text-zinc-400">Belum ada transaksi di {s.monthLabel}.</p>
          {onSetup && (
            <button onClick={onSetup} className="mt-3 text-xs font-semibold text-emerald-600" data-testid="summary-empty-setup">
              Atur Target Hemat di Pengaturan Keuangan →
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Status */}
          <div className="mb-4 flex items-center justify-between">
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS[s.status.level]}`} data-testid="summary-status">
              {s.status.label}
            </span>
            {s.prev.available && s.prev.changePct != null ? (
              <span className="text-xs text-zinc-500" data-testid="summary-change">
                Pengeluaran {s.prev.changePct > 0 ? "naik" : "turun"} {Math.abs(Math.round(s.prev.changePct * 100))}% vs bulan lalu
              </span>
            ) : (
              <span className="text-xs text-zinc-400" data-testid="summary-no-compare">Data pembanding belum cukup</span>
            )}
          </div>

          {/* Income / Expense / Net */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-2xl bg-emerald-50 p-3">
              <div className="flex items-center gap-1 text-emerald-700">
                <TrendingUp className="h-3 w-3" /><span className="text-[10px] font-semibold">Masuk</span>
              </div>
              <p className="mt-1 whitespace-nowrap font-mono text-sm font-bold text-emerald-700" data-testid="summary-income">{formatCompact(s.income)}</p>
            </div>
            <div className="rounded-2xl bg-rose-50 p-3">
              <div className="flex items-center gap-1 text-rose-600">
                <TrendingDown className="h-3 w-3" /><span className="text-[10px] font-semibold">Keluar</span>
              </div>
              <p className="mt-1 whitespace-nowrap font-mono text-sm font-bold text-rose-600" data-testid="summary-expense">{formatCompact(s.expense)}</p>
            </div>
            <div className="rounded-2xl bg-zinc-50 p-3">
              <span className="text-[10px] font-semibold text-zinc-500">{s.net >= 0 ? "Surplus" : "Defisit"}</span>
              <p className={`mt-1 whitespace-nowrap font-mono text-sm font-bold ${s.net >= 0 ? "text-zinc-900" : "text-rose-600"}`} data-testid="summary-net">
                {formatCompact(Math.abs(s.net))}
              </p>
            </div>
          </div>

          {/* Key metrics */}
          <div className="mt-3 grid grid-cols-4 gap-2">
            <Metric Icon={Percent} label="Nabung" testId="summary-saving-rate"
              value={pctStr(s.savingRate)} valueClass={s.savingRate != null && s.savingRate < 0 ? "text-rose-600" : "text-zinc-900"} />
            <Metric Icon={CreditCard} label="Rasio cicilan" testId="summary-debt-ratio"
              value={pctStr(s.debtRatio)} valueClass={s.debtRatio != null && s.debtRatio > 0.35 ? "text-amber-600" : "text-zinc-900"} />
            <Metric Icon={PiggyBank} label="Dana darurat" testId="summary-emergency"
              value={s.emergency.available ? `${s.emergency.months >= 6 ? "6+" : s.emergency.months.toFixed(1).replace(".", ",")} bln` : "–"} />
            <Metric Icon={Activity} label="Skor rata2" testId="summary-avg-health"
              value={s.avgHealth ? `${s.avgHealth.value}/100` : "–"} valueClass={s.avgHealth ? BAND_TEXT[s.avgHealth.band] : "text-zinc-400"} />
          </div>

          {/* Plain-language explanations so numbers aren't confusing */}
          <div className="mt-3 space-y-1.5 rounded-2xl bg-zinc-50 p-3 text-xs leading-relaxed text-zinc-600" data-testid="summary-explain">
            {s.emergency.available ? (
              <p data-testid="summary-emergency-explain">
                <span className="font-semibold text-zinc-800">Dana darurat saat ini: {formatCompact(s.emergency.amount)}</span>{" "}
                (setara {s.emergency.months >= 6 ? "6+" : s.emergency.months.toFixed(1).replace(".", ",")} bulan {emergencyBasisLabel(s.emergency.basis)}).
              </p>
            ) : s.emergency.amount > 0 ? (
              <p data-testid="summary-emergency-explain">
                <span className="font-semibold text-zinc-800">Dana darurat saat ini: {formatCompact(s.emergency.amount)}</span>{" "}
                — belum bisa dihitung setara berapa bulan karena pengeluaran wajib/pola belanja belum tersedia.
              </p>
            ) : (
              <p data-testid="summary-emergency-explain">Dana darurat: <span className="font-semibold text-zinc-800">Belum tersedia</span>. Isi jumlahnya di Pengaturan Keuangan agar bisa dihitung ketahanannya.</p>
            )}
            {s.savingRate != null && (
              s.savingRate < 0 ? (
                <p data-testid="summary-saving-explain">
                  Bulan ini bukannya menabung, malah defisit setara {Math.abs(Math.round(s.savingRate * 100))}% dari pemasukan — sekitar {formatCompact(-s.net)} diambil dari tabungan/dana lain.
                </p>
              ) : (
                <p data-testid="summary-saving-explain">
                  Bulan ini Anda menyisihkan sekitar {Math.round(s.savingRate * 100)}% dari pemasukan ({formatCompact(s.net)}).
                </p>
              )
            )}
            {s.debtRatio != null && (
              <p data-testid="summary-debt-explain">
                Cicilan menyerap sekitar {Math.round(s.debtRatio * 100)}% dari pemasukan bulan ini.
              </p>
            )}
          </div>

          {/* Top 5 categories */}
          <div className="mt-5" data-testid="summary-top-categories">
            <p className="mb-2 text-xs font-semibold text-zinc-500">
              {s.topCategories.length > 1 ? `${s.topCategories.length} Pengeluaran Terbesar` : "Pengeluaran Terbesar"}
            </p>
            <div className="space-y-2">
              {s.topCategories.map((c) => (
                <div key={c.name} data-testid={`summary-cat-${c.name}`}>
                  <div className="mb-1 flex items-baseline justify-between">
                    <span className="text-xs font-semibold text-zinc-800">{c.name}</span>
                    <span className="font-mono text-xs text-zinc-500">{formatCompact(c.amount)} · {Math.round(c.pct * 100)}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                    <div className="h-full rounded-full bg-zinc-800" style={{ width: `${Math.max(Math.round(c.pct * 100), 3)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Target vs realisasi */}
          {s.budgetRows.length > 0 && (
            <div className="mt-5" data-testid="summary-budgets">
              <p className="mb-2 text-xs font-semibold text-zinc-500">Target vs Realisasi</p>
              <div className="space-y-1.5">
                {s.budgetRows.map((b) => (
                  <div key={b.category} className="flex items-center justify-between gap-2 text-xs" data-testid={`summary-budget-${b.category}`}>
                    <span className="min-w-0 truncate font-semibold text-zinc-700">{b.category}</span>
                    <span className={`shrink-0 whitespace-nowrap font-mono ${b.over > 0 ? "text-rose-600" : "text-zinc-500"}`}>
                      {formatCompact(b.used)} / {formatCompact(b.target)}
                      {b.over > 0 && ` · lewat ${formatCompact(b.over)}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Insights */}
          {s.insights.length > 0 && (
            <div className="mt-5 rounded-2xl bg-zinc-50 p-4" data-testid="summary-insights">
              <p className="mb-2 text-xs font-semibold text-zinc-500">Insight Utama</p>
              <ul className="space-y-1.5">
                {s.insights.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs leading-relaxed text-zinc-700" data-testid={`summary-insight-${i}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-800" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4" data-testid="summary-recommendations">
            <div className="mb-2 flex items-center gap-1.5 text-emerald-700">
              <Lightbulb className="h-3.5 w-3.5" />
              <p className="text-xs font-semibold">Rekomendasi Bulan Depan</p>
            </div>
            <ul className="space-y-1.5">
              {s.recommendations.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-xs leading-relaxed text-emerald-800" data-testid={`summary-rec-${i}`}>
                  <ArrowRight className="mt-0.5 h-3 w-3 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {s.budgetRows.length === 0 && onSetup && (
            <button onClick={onSetup} className="mt-3 text-xs font-semibold text-emerald-600" data-testid="summary-setup-link">
              Atur Target Hemat untuk perbandingan target →
            </button>
          )}
        </>
      )}
    </div>
  );
}
